import React, { useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES } from '../../../../utils/appConstant';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import AuthService from '../../../../services/auth.service';
import { Validation } from '../../../../interfaces/common';
import { ResetPassword } from '../../../../interfaces/auth.interface';
import { Modules } from '../../../../enums';
import { validateResetPasswordForm } from '../validations';

const Form: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [formData, setFormData] = useState<ResetPassword>({ newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<Validation>({ validate: true, fields: [] });
  const [showPassword, setShowPassword] = useState<any>({
    newPassword: false,
    confirmPassword: false,
  });

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      setFormData((prev) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setFormData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let newValidation = validateResetPasswordForm(
        {
          ...formData,
          [name]: event.target.value.trim(),
        },
        name
      );
      setValidation(newValidation);
    },
    [formData.newPassword, formData.confirmPassword]
  );

  const handleSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event && event.preventDefault();

      const newValidation = validateResetPasswordForm(formData);
      setValidation(newValidation);

      if (!newValidation.validate) return;

      setLoading(true);

      try {
        const data: any = await AuthService.resetPassword({
          token: searchParams.get('token') || '',
          password: formData.newPassword && formData.newPassword.trim(),
        });

        if (data.success !== false) {
          toast.success(<Toast message={APP_SUCCESS_MESSAGES.resetPassword} />, { icon: false });
          setLoading(false);
          navigate(Modules.auth);
        }
      } catch (err: any) {
        setLoading(false);
      }
    },
    [formData.newPassword, formData.confirmPassword]
  );

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') handleSubmit();
    },
    [handleSubmit]
  );

  const handleShowPasswordChange = useCallback(
    (name: any) => {
      setShowPassword((prev: any) => {
        return {
          ...prev,
          [name]: !showPassword[name],
        };
      });
    },
    [showPassword.confirmPassword, showPassword.newPassword]
  );

  return (
    <form id="formAuthentication" className="mb-5" autoComplete="off">
      <div className="mb-5">
        <div className="form-password-toggle">
          <div className="input-group input-group-merge">
            <div className="form-floating form-floating-outline">
              <Input
                type={showPassword.newPassword ? 'text' : 'password'}
                id={VALIDATION_FIELD_MAPPING.newPassword}
                name={VALIDATION_FIELD_MAPPING.newPassword}
                error={
                  !!validation.fields.find(
                    (field) => field.field === VALIDATION_FIELD_MAPPING.newPassword
                  )
                }
                errorMessage={
                  validation.fields.find(
                    (field) => field.field === VALIDATION_FIELD_MAPPING.newPassword
                  )?.message ?? ''
                }
                placeholder={t('resetPassword.pageTexts.form.newPassword.placeholder')}
                label={t('resetPassword.pageTexts.form.newPassword.label')}
                value={formData.newPassword}
                onKeyDown={handleKeyDown}
                onBlur={handleInputBlur}
                onChange={handleInputChange}
              />
            </div>
            <span
              className="password-eye-icon input-group-text cursor-pointer"
              onClick={() =>
                handleShowPasswordChange(
                  VALIDATION_FIELD_MAPPING.newPassword as keyof ResetPassword
                )
              }
            >
              <i className={showPassword.newPassword ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
            </span>
          </div>
        </div>
      </div>
      <div className="mb-5">
        <div className="form-password-toggle">
          <div className="input-group input-group-merge">
            <div className="form-floating form-floating-outline">
              <Input
                type={showPassword.confirmPassword ? 'text' : 'password'}
                id={VALIDATION_FIELD_MAPPING.confirmPassword}
                name={VALIDATION_FIELD_MAPPING.confirmPassword}
                error={
                  !!validation.fields.find(
                    (field) => field.field === VALIDATION_FIELD_MAPPING.confirmPassword
                  )
                }
                errorMessage={
                  validation.fields.find(
                    (field) => field.field === VALIDATION_FIELD_MAPPING.confirmPassword
                  )?.message ?? ''
                }
                placeholder={t('resetPassword.pageTexts.form.confirmPassword.placeholder')}
                label={t('resetPassword.pageTexts.form.confirmPassword.label')}
                value={formData.confirmPassword}
                onKeyDown={handleKeyDown}
                onBlur={handleInputBlur}
                onChange={handleInputChange}
              />
            </div>
            <span
              className="password-eye-icon input-group-text cursor-pointer"
              onClick={() =>
                handleShowPasswordChange(
                  VALIDATION_FIELD_MAPPING.confirmPassword as keyof ResetPassword
                )
              }
            >
              <i className={showPassword.confirmPassword ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
            </span>
          </div>
        </div>
      </div>
      <Button
        className="btn btn-primary d-grid w-100"
        text={t('resetPassword.pageTexts.form.resetPasswordButton')}
        disabled={loading}
        loading={loading}
        onClick={handleSubmit}
      />
    </form>
  );
};

export default Form;
